/**
 * Drop-to-Think staging helpers (Think host M5).
 *
 * Validation + draft preservation live here so dock tests do not need a DOM.
 * Upload stages to POST …/hardware/uploads; the message file part is
 * `pcbx-artifact://art_…` — Think later decides to call import_project.
 */

export const THINK_ZIP_ACCEPT = ".zip,application/zip";
export const THINK_ZIP_MAX_BYTES = 50 * 1024 * 1024;
export const ARTIFACT_URI_SCHEME = "pcbx-artifact://";

export const THINK_DROP_REJECT = {
  accept: "Not a zip — attach a KiCad project .zip.",
  max_file_size: "Too large — zip must be 50 MiB or smaller.",
  duplicate: "Already uploaded — use the existing artifact.",
} as const;

export const THINK_DROP_OVERLAY_TITLE = "Drop to stage";
export const THINK_DROP_OVERLAY_HINT = "Think decides when to import";
export const THINK_DROP_HINT = "Drop… anywhere on this panel";
export const THINK_STATUS_THINKING = "Thinking…";
export const THINK_STATUS_READY = "Ready";
export const THINK_STATUS_ERROR = "Couldn't reply";

export function thinkComposerStatus(status?: string | null): string {
  if (status === "submitted" || status === "streaming") return THINK_STATUS_THINKING;
  if (status === "error") return THINK_STATUS_ERROR;
  return THINK_DROP_HINT;
}
export const THINK_ZIP_PICKER_LABEL = "Attach .zip · ≤ 50 MiB";
export const THINK_OK = "#3D9B6A";
/** ≥4.5:1 on #0C0C0C / #000 at 11px (plate muted #545457 is ~2.5:1). */
export const THINK_HINT_COLOR = "#8B8B90";

export const THINK_REJECT_TEXT = "#F2B8BB";
export const THINK_REJECT_BG = "color-mix(in srgb, #E31C25 7%, #0C0C0C)";
export const THINK_REJECT_BORDER = "color-mix(in srgb, #E31C25 35%, transparent)";

export type UploadProgress = { loaded: number; total: number };

export function formatUploadProgress(loaded: number, total: number): string {
  const mb = (n: number) => {
    const v = n / (1024 * 1024);
    return v >= 10 ? v.toFixed(0) : v.toFixed(1);
  };
  const denom = total > 0 ? total : loaded;
  const pct = denom > 0 ? Math.round((loaded / denom) * 100) : 0;
  return `${mb(loaded)} / ${mb(denom)} MB · ${pct}%`;
}

export type ThinkDropRejectCode = keyof typeof THINK_DROP_REJECT;

export type ThinkDraft = {
  text: string;
};

export type ThinkDropInputState = {
  draft: ThinkDraft;
  error: string | null;
  existingArtifactId: string | null;
  existingPathCount: number | null;
};

export type ThinkFilePart = {
  type: "file";
  filename: string;
  mediaType: "application/zip";
  url: string;
};

export function isThinkZipName(name: string): boolean {
  return name.toLowerCase().endsWith(".zip");
}

export function isThinkZipType(type: string): boolean {
  const t = type.toLowerCase();
  return t === "application/zip" || t === "application/x-zip-compressed";
}

export function isThinkZipFile(file: { name: string; type: string }): boolean {
  return isThinkZipName(file.name) || isThinkZipType(file.type);
}

export function rejectThinkDropFile(file: {
  name: string;
  type: string;
  size: number;
}): { code: ThinkDropRejectCode; message: string } | null {
  if (!isThinkZipFile(file)) {
    return { code: "accept", message: THINK_DROP_REJECT.accept };
  }
  if (file.size > THINK_ZIP_MAX_BYTES) {
    return { code: "max_file_size", message: THINK_DROP_REJECT.max_file_size };
  }
  return null;
}

/** Shared zip-only / ≤50 MiB gate for PromptInput, drop, and the hidden dock input. */
export function validateThinkZipFile(file: {
  name: string;
  type: string;
  size: number;
}): { ok: true } | { ok: false; code: ThinkDropRejectCode; message: string } {
  const denied = rejectThinkDropFile(file);
  if (denied) return { ok: false, ...denied };
  return { ok: true };
}

type ThinkZipRejectListener = (message: string) => void;
let rejectListener: ThinkZipRejectListener | null = null;

export function reportThinkZipRejection(message: string): void {
  rejectListener?.(message);
}

export function listenThinkZipRejection(fn: ThinkZipRejectListener): () => void {
  rejectListener = fn;
  return () => {
    if (rejectListener === fn) rejectListener = null;
  };
}

/** Rejection never clears the compose draft. */
export function applyThinkDropRejection(
  state: ThinkDropInputState,
  message: string,
  existingArtifactId: string | null = null,
  existingPathCount: number | null = null,
): ThinkDropInputState {
  return {
    draft: { text: state.draft.text },
    error: message,
    existingArtifactId,
    existingPathCount,
  };
}

export function artifactUri(artifactId: string): string {
  const id = artifactId.startsWith("art_") ? artifactId : artifactId;
  return `${ARTIFACT_URI_SCHEME}${id}`;
}

export function dedupeThinkZipChips<T extends { filename?: string; id?: string }>(
  files: T[],
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const file of files) {
    const key = (file.filename || file.id || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(file);
  }
  return out;
}

export function thinkFilePart(artifactId: string, filename: string): ThinkFilePart {
  return {
    type: "file",
    filename,
    mediaType: "application/zip",
    url: artifactUri(artifactId),
  };
}

export function parseDuplicateArtifactId(body: unknown): string | null {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  const id = (body as { artifact_id?: unknown }).artifact_id;
  return typeof id === "string" && id.startsWith("art_") ? id : null;
}

/** Best-effort name during drag — browsers often hide FileList until drop. */
/** Native capture so StickToBottom / message text children still accept the drop. */
export function preventThinkDropDefault(event: Event): void {
  event.preventDefault();
  const dt = (event as DragEvent).dataTransfer;
  if (dt) dt.dropEffect = dt.effectAllowed === "move" ? "move" : "copy";
}

export const THINK_DROP_CATCHER = "think-drop-catcher";

function eventInside(node: Element, event: Event): boolean {
  const target = event.target;
  if (target instanceof Node && node.contains(target)) return true;
  const current = event.currentTarget;
  return current === node || current === document;
}

function armDropCatcher(root: Element): void {
  const catcher = root.querySelector<HTMLElement>(`[data-testid="${THINK_DROP_CATCHER}"]`);
  if (catcher) catcher.style.pointerEvents = "auto";
}

function disarmDropCatcher(root: Element): void {
  const catcher = root.querySelector<HTMLElement>(`[data-testid="${THINK_DROP_CATCHER}"]`);
  if (catcher) catcher.style.pointerEvents = "none";
}

/**
 * Whole-panel drop target. Document capture so Playwright native dragTo onto
 * a text node still preventDefault's dragover. Arms a transparent catcher
 * synchronously on dragenter so the next dragover hits the overlay.
 */
export function attachThinkDropSurface(node: EventTarget | null | undefined): () => void {
  if (!node || typeof (node as HTMLElement).addEventListener !== "function") {
    return () => {};
  }
  const target = node as HTMLElement;
  const onDrag = (event: Event) => {
    if (!eventInside(target, event)) return;
    preventThinkDropDefault(event);
    if (event.type === "dragenter" || event.type === "dragover") armDropCatcher(target);
    if (event.type === "drop") disarmDropCatcher(target);
  };
  const onLeave = (event: Event) => {
    if (!eventInside(target, event)) return;
    const next = (event as DragEvent).relatedTarget;
    if (next instanceof Node && target.contains(next)) return;
    disarmDropCatcher(target);
  };
  document.addEventListener("dragenter", onDrag, true);
  document.addEventListener("dragover", onDrag, true);
  document.addEventListener("drop", onDrag, true);
  document.addEventListener("dragleave", onLeave, true);
  target.addEventListener("dragenter", onDrag, true);
  target.addEventListener("dragover", preventThinkDropDefault, true);
  target.addEventListener("drop", preventThinkDropDefault, true);
  return () => {
    document.removeEventListener("dragenter", onDrag, true);
    document.removeEventListener("dragover", onDrag, true);
    document.removeEventListener("drop", onDrag, true);
    document.removeEventListener("dragleave", onLeave, true);
    target.removeEventListener("dragenter", onDrag, true);
    target.removeEventListener("dragover", preventThinkDropDefault, true);
    target.removeEventListener("drop", preventThinkDropDefault, true);
    disarmDropCatcher(target);
  };
}

export function thinkDropHoverName(dt: {
  files?: ArrayLike<{ name?: string }>;
  items?: ArrayLike<{ kind?: string; getAsFile?: () => { name?: string } | null }>;
} | null | undefined): string | null {
  if (!dt) return null;
  const file = dt.files && dt.files.length > 0 ? dt.files[0] : null;
  if (file?.name) return file.name;
  const item = dt.items && dt.items.length > 0 ? dt.items[0] : null;
  if (item?.kind === "file") {
    const asFile = item.getAsFile?.();
    if (asFile?.name) return asFile.name;
  }
  return null;
}
